import { motion } from "framer-motion";
import { Play, ShieldCheck } from "lucide-react";
import {
  GF_URL,
  PLAY_STORE_URL,
  VARIANT,
  VIP_ACQUISTA_URL,
  VIP_LOGIN_URL,
  VIP_REGISTER_URL,
} from "@/constants/links";
import { track } from "@/lib/tracker";

const Star = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M12 2l2.4 6.9L21 11l-6.6 2.1L12 20l-2.4-6.9L3 11l6.6-2.1L12 2z" />
  </svg>
);

const isVip = VARIANT === "vip";

export const FinalCta = () => (
  <section id="download" className="scroll-mt-24 pb-24 md:pb-32">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.15 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="relative overflow-hidden rounded-[3rem] bg-ink px-8 py-16 text-center md:px-16 md:py-24"
      >
        <div aria-hidden="true" className="absolute inset-0 bg-dots-light opacity-50" />
        <div
          aria-hidden="true"
          className="absolute -bottom-32 left-1/2 h-72 w-[560px] -translate-x-1/2 rounded-full blur-3xl"
          style={{ background: "radial-gradient(closest-side, rgba(119,99,229,0.55), transparent)" }}
        />
        <Star className="animate-float-slow absolute left-[10%] top-12 hidden h-9 w-9 text-yellow md:block" />
        <Star className="animate-float absolute bottom-14 right-[12%] hidden h-6 w-6 text-pinky md:block" />

        <div className="relative">
          <span className="inline-flex items-center gap-2 rounded-full bg-yellow/15 px-4 py-2 text-xs font-bold uppercase tracking-widest text-yellow">
            {isVip ? "Dashboard VIP" : "Inizia oggi"}
          </span>
          <h2 className="mx-auto mt-5 max-w-2xl font-display text-3xl font-black tracking-tight text-white sm:text-4xl lg:text-5xl">
            {isVip
              ? "Attiva la supervisione avanzata, oggi."
              : "Pronto a vivere la tecnologia con più serenità?"}
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-base font-medium leading-relaxed text-white/70 sm:text-lg">
            {isVip
              ? "Crea l'account VIP, associa il dispositivo del figlio e gestisci tutto dalla dashboard dedicata."
              : "Crea la tua famiglia, associa i dispositivi con un codice a 4 cifre e imposta le prime regole insieme ai tuoi figli. In pochi minuti."}
          </p>

          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            {isVip ? (
              <>
                <a
                  data-testid="final-login-btn"
                  href={VIP_LOGIN_URL}
                  onClick={() => track("login_click")}
                  className="btn-3d-yellow inline-flex items-center gap-3 rounded-full px-8 py-4 text-base font-extrabold"
                >
                  <ShieldCheck size={20} />
                  Accedi alla dashboard
                </a>
                <a
                  data-testid="final-register-btn"
                  href={VIP_REGISTER_URL}
                  onClick={() => track("register_click")}
                  className="btn-3d-violet inline-flex items-center rounded-full px-8 py-4 text-base font-extrabold"
                >
                  Crea account VIP
                </a>
              </>
            ) : (
              <>
                <a
                  data-testid="final-playstore-btn"
                  href={PLAY_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => track("play_click")}
                  className="btn-3d-yellow inline-flex items-center gap-3 rounded-full px-8 py-4 text-base font-extrabold"
                >
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-ink text-yellow">
                    <Play size={14} className="fill-current" />
                  </span>
                  Scarica da Google Play
                </a>
                <a
                  data-testid="final-vip-info-btn"
                  href="https://vip.guardianfamily.it"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => track("vip_link_click")}
                  className="btn-3d-violet inline-flex items-center rounded-full px-8 py-4 text-base font-extrabold"
                >
                  Scopri la versione VIP
                </a>
              </>
            )}
          </div>

          {isVip ? (
            <div className="mt-7 space-y-2">
              <a
                data-testid="final-acquista-link"
                href={VIP_ACQUISTA_URL}
                onClick={() => track("acquista_click")}
                className="inline-block text-sm font-semibold text-white/70 underline decoration-white/30 underline-offset-4 transition-colors hover:text-white"
              >
                Oppure acquista il piano VIP
              </a>
              <p className="text-xs font-medium text-white/50">
                Prodotto separato con dashboard e account dedicati, indipendenti da Guardian Family
                standard ·{" "}
                <a
                  data-testid="final-standard-link"
                  href={GF_URL}
                  onClick={() => track("standard_link_click")}
                  className="underline underline-offset-2 hover:text-white"
                >
                  guardianfamily.it
                </a>
              </p>
            </div>
          ) : (
            <p className="mt-7 text-xs font-medium text-white/50">
              Guardian Family è disponibile su Google Play a 3,89 € al mese · Cerchi la supervisione
              avanzata?{" "}
              <a
                data-testid="final-vip-link"
                href="https://vip.guardianfamily.it"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => track("vip_link_click")}
                className="underline underline-offset-2 hover:text-white"
              >
                Scopri Guardian Family VIP
              </a>
            </p>
          )}
        </div>
      </motion.div>
    </div>
  </section>
);
